/**
 * IosVideoWarning — banner shown to iOS users on video-proof roll requests
 * (TICKET-281c). Dismissible; preference persisted in localStorage.
 *
 * @module components/IosVideoWarning
 */

import { useState } from 'react';
import { isIos } from '@/utils/platform';

const STORAGE_KEY = 'iosVideoWarningDismissed';

/** Props for {@link IosVideoWarning}. */
export interface IosVideoWarningProps {
  /** The current roll request's proof mode. */
  proofMode: 'image' | 'video' | 'either' | 'none';
}

/**
 * Render an amber warning when the user is on iOS and the current roll
 * request requires (or allows) video proof. No-op for non-iOS, image-only,
 * none, or already-dismissed cases.
 */
export function IosVideoWarning({ proofMode }: IosVideoWarningProps) {
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1'
  );
  if (dismissed) return null;
  if (proofMode === 'image' || proofMode === 'none') return null;
  if (!isIos()) return null;

  return (
    <div
      className="mt-3 rounded-md border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-100"
      role="alert"
    >
      <p className="mb-2">
        Video proof may not work reliably on iOS. Use image capture if you have trouble.
      </p>
      <button
        type="button"
        className="text-xs underline opacity-80 hover:opacity-100"
        onClick={() => {
          try {
            localStorage.setItem(STORAGE_KEY, '1');
          } catch {
            /* ignore quota / privacy-mode errors */
          }
          setDismissed(true);
        }}
      >
        Got it, dismiss
      </button>
    </div>
  );
}
